import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus, faTrash, faShoppingBag } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import AuthenticateService from './AuthenticateService';
import Sidebar from './Sidebar';
import Header from './Header';
import Footer from './Footer';
import PreLoader from './extra/PreLoader';
import { cartStorage } from './services/cartStorage';

const money = (n) => `₦${(parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Cart = () => {
  const { http, user, logout } = AuthenticateService();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState(cartStorage.getItems());

  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [settings, setSettings] = useState({ pod_enabled: false, bnpl_enabled: false, bnpl_max_months: 0, bnpl_percent_per_month: 0 });

  const [deliveryMethod, setDeliveryMethod] = useState('pickup');
  const [paymentMethod, setPaymentMethod] = useState('wallet');
  const [bnplMonths, setBnplMonths] = useState('');
  const [account, setAccount] = useState('');
  const [savedContactId, setSavedContactId] = useState('');
  const [address, setAddress] = useState('');
  const [contact, setContact] = useState('');
  const [promoInput, setPromoInput] = useState('');
  const [appliedCode, setAppliedCode] = useState('');
  const [promoMessage, setPromoMessage] = useState(null);
  const [promoError, setPromoError] = useState(null);

  const [quote, setQuote] = useState({ subtotal: 0, discount: 0, delivery_fee: 0, total: 0 });
  const [quoting, setQuoting] = useState(false);
  const [placing, setPlacing] = useState(false);

  const quoteTimer = useRef(null);

  useEffect(() => {
    document.title = 'My Cart';
    init();
    const unsubscribe = cartStorage.subscribe(() => setItems(cartStorage.getItems()));
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loading) return;
    if (quoteTimer.current) clearTimeout(quoteTimer.current);
    quoteTimer.current = setTimeout(() => requote(), 400);
    return () => clearTimeout(quoteTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, deliveryMethod, appliedCode]);

  const init = async () => {
    setLoading(true);
    try {
      const [itemsRes, detailsRes] = await Promise.all([
        http.post('/ecommerce/items', { page: 1, category: 0 }),
        http.get('/users/get-details'),
      ]);

      if (itemsRes.data.status === true) {
        setContacts(itemsRes.data.contacts || []);
        setSettings(itemsRes.data.ecommerceSettings || settings);
        const removed = cartStorage.sync(itemsRes.data.product || []);
        setItems(cartStorage.getItems());
        if (removed.length > 0) {
          toast.warning(`No longer available and removed from your cart: ${removed.join(', ')}`);
        }
      }

      if (detailsRes.data.status === true) {
        const accountsData = detailsRes.data.data?.accounts || [];
        setAccounts(accountsData);
        if (accountsData.length > 0) setAccount(accountsData[0].virtual_accnum);
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        logout();
        return;
      }
      toast.error('Could not load your cart, please try again');
    } finally {
      setLoading(false);
    }
    // `loading` isn't in the requote effect's dependency list (see below), so the first quote after
    // mount has to be requested explicitly here rather than left to that effect.
    requote();
  };

  // Reads straight from storage rather than the `items` state so a quote requested right after a
  // cartStorage mutation (e.g. the sync() inside init()) never races a not-yet-applied setState.
  const payloadItems = () => cartStorage.getItems().map((i) => ({ product_id: i.id, qrty: i.qty }));

  const requote = async (opts = {}) => {
    if (cartStorage.getItems().length === 0) {
      setQuote({ subtotal: 0, discount: 0, delivery_fee: 0, total: 0 });
      return;
    }
    setQuoting(true);
    try {
      const body = { items: payloadItems(), deliveryMethod };
      if (appliedCode) body.promo_code = appliedCode;
      const res = await http.post('/ecommerce/cart-quote', body);
      if (res.data.status === true) {
        setQuote(res.data.data);
        if (appliedCode) setPromoMessage(res.data.data.promo_message);
        setPromoError(null);
      } else {
        throw new Error(res.data.message);
      }
    } catch (error) {
      const message = error?.response?.data?.message || error.message || 'Could not price your cart';
      if (appliedCode) {
        // A bad/expired promo must not block the rest of the cart: drop it and price again without it.
        setPromoError(message);
        setPromoMessage(null);
        setAppliedCode('');
      } else if (opts.announce) {
        toast.error(message);
      }
    } finally {
      setQuoting(false);
    }
  };

  const applyPromo = () => {
    const code = promoInput.trim();
    if (!code) return;
    setPromoError(null);
    setAppliedCode(code);
  };

  const removePromo = () => {
    setAppliedCode('');
    setPromoInput('');
    setPromoMessage(null);
    setPromoError(null);
  };

  const bnplMarkupPercent = () => {
    const perMonth = parseFloat(settings.bnpl_percent_per_month) || 0;
    const months = parseFloat(bnplMonths) || 0;
    return perMonth * months;
  };

  const payable = () => {
    const total = parseFloat(quote.total) || 0;
    return paymentMethod === 'bnpl' ? total * (1 + bnplMarkupPercent() / 100) : total;
  };

  const placeOrder = async () => {
    if (items.length === 0 || placing) return;
    if (!account) return toast.error('No wallet account found.');
    if (paymentMethod === 'bnpl' && !bnplMonths) return toast.error('Please select a repayment duration.');
    const usingSaved = !!savedContactId;
    if (!usingSaved && (!address.trim() || !contact.trim())) {
      return toast.error('Please enter your delivery address and contact number.');
    }

    setPlacing(true);
    try {
      const body = {
        items: payloadItems(),
        account_id: account,
        paymentMethod,
        deliveryMethod,
        haveContact: usingSaved ? savedContactId : null,
        address: address.trim(),
        contact: contact.trim(),
      };
      if (paymentMethod === 'bnpl') body.bnplMonths = bnplMonths;
      if (appliedCode) body.promo_code = appliedCode;

      const res = await http.post('/ecommerce/checkout', body);
      if (res.data.status === true) {
        cartStorage.clear();
        await Swal.fire({
          title: res.data.message || 'Order placed',
          text: `Total: ${money(res.data.total)}`,
          icon: 'success',
          confirmButtonText: 'View my orders',
        });
        navigate('/my-orders');
      } else {
        toast.error(res.data.message || 'Order failed');
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        logout();
        return;
      }
      toast.error(error?.response?.data?.message || 'Order failed, please try again');
      init(); // stock or price may have changed; refresh so the cart shows the truth
    } finally {
      setPlacing(false);
    }
  };

  const monthOptions = Array.from({ length: parseInt(settings.bnpl_max_months, 10) || 0 }, (_, i) => i + 1);

  return (
    <>
      {loading ? (
        <PreLoader />
      ) : (
        <>
          <Sidebar />
          <Header user={user} />

          <div className="content">
            <h4 className="fs-1 mb-4">My Cart</h4>

            {items.length === 0 ? (
              <div className="text-center py-5">
                <FontAwesomeIcon icon={faShoppingBag} size="3x" className="text-500 mb-3" />
                <p className="text-700">Your cart is empty.</p>
                <Link to="/store" className="btn btn-primary">Continue Shopping</Link>
              </div>
            ) : (
              <div className="row g-4">
                <div className="col-12 col-lg-7">
                  <div className="card">
                    <div className="card-body">
                      {items.map((item) => (
                        <div key={item.id} className="d-flex align-items-center border-bottom py-3" style={{ gap: '12px' }}>
                          <img
                            src={item.image}
                            alt={item.name}
                            style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8 }}
                            onError={(e) => { e.target.onerror = null; e.target.src = '/logo.png'; }}
                          />
                          <div className="flex-grow-1">
                            <div className="fw-semi-bold">{item.name}</div>
                            <div className="text-500 fs--1">{money(item.price)} each</div>
                          </div>
                          <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => cartStorage.setQty(item.id, item.qty - 1)}
                            >
                              <FontAwesomeIcon icon={faMinus} />
                            </button>
                            <span style={{ minWidth: 24, textAlign: 'center' }}>{item.qty}</span>
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              disabled={item.stock > 0 && item.qty >= item.stock}
                              onClick={() => cartStorage.setQty(item.id, item.qty + 1)}
                            >
                              <FontAwesomeIcon icon={faPlus} />
                            </button>
                          </div>
                          <div className="fw-bold text-end" style={{ minWidth: 90 }}>{money(item.price * item.qty)}</div>
                          <button
                            type="button"
                            className="btn btn-link text-danger"
                            onClick={() => cartStorage.remove(item.id)}
                            title="Remove"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="card mt-4">
                    <div className="card-body">
                      <h6 className="mb-3">Delivery</h6>
                      <div className="btn-group mb-3" role="group">
                        <button
                          type="button"
                          className={`btn ${deliveryMethod === 'pickup' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setDeliveryMethod('pickup')}
                        >
                          Pickup
                        </button>
                        <button
                          type="button"
                          className={`btn ${deliveryMethod === 'delivery' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setDeliveryMethod('delivery')}
                        >
                          Delivery
                        </button>
                      </div>

                      {deliveryMethod === 'delivery' && (
                        <>
                          {contacts.length > 0 && (
                            <div className="mb-3">
                              <label className="form-label">Use a saved address</label>
                              <select
                                className="form-select"
                                value={savedContactId}
                                onChange={(e) => setSavedContactId(e.target.value)}
                              >
                                <option value="">Enter a new address</option>
                                {contacts.map((c) => (
                                  <option key={c.id} value={c.id}>{c.address} ({c.contact})</option>
                                ))}
                              </select>
                            </div>
                          )}
                          {!savedContactId && (
                            <div className="row g-3">
                              <div className="col-md-6">
                                <div className="form-floating">
                                  <input
                                    className="form-control"
                                    id="cartAddress"
                                    placeholder="Delivery Address"
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                  />
                                  <label htmlFor="cartAddress">Delivery Address</label>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="form-floating">
                                  <input
                                    className="form-control"
                                    id="cartContact"
                                    placeholder="Contact Phone Number"
                                    value={contact}
                                    onChange={(e) => setContact(e.target.value)}
                                  />
                                  <label htmlFor="cartContact">Contact Phone Number</label>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-lg-5">
                  <div className="card">
                    <div className="card-body">
                      <h6 className="mb-3">Pay From</h6>
                      {accounts.length > 0 ? (
                        <select className="form-select mb-4" value={account} onChange={(e) => setAccount(e.target.value)}>
                          {accounts.map((a) => (
                            <option key={a.id} value={a.virtual_accnum}>
                              {a.virtual_accnum} — {money(a.user_wallet?.balance)}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="alert alert-warning">No wallet account found.</div>
                      )}

                      <h6 className="mb-3">Payment Method</h6>
                      <div className="d-flex flex-column mb-4" style={{ gap: '8px' }}>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="paymentMethod"
                            id="payWallet"
                            checked={paymentMethod === 'wallet'}
                            onChange={() => setPaymentMethod('wallet')}
                          />
                          <label className="form-check-label" htmlFor="payWallet">Pay from wallet now</label>
                        </div>
                        {settings.pod_enabled && (
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="paymentMethod"
                              id="payPod"
                              checked={paymentMethod === 'pay_on_delivery'}
                              onChange={() => setPaymentMethod('pay_on_delivery')}
                            />
                            <label className="form-check-label" htmlFor="payPod">Pay on delivery</label>
                          </div>
                        )}
                        {settings.bnpl_enabled && (
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="paymentMethod"
                              id="payBnpl"
                              checked={paymentMethod === 'bnpl'}
                              onChange={() => setPaymentMethod('bnpl')}
                            />
                            <label className="form-check-label" htmlFor="payBnpl">Buy Now, Pay Later</label>
                          </div>
                        )}
                      </div>

                      {paymentMethod === 'bnpl' && (
                        <div className="mb-4">
                          <label className="form-label">Repayment duration</label>
                          <select className="form-select" value={bnplMonths} onChange={(e) => setBnplMonths(e.target.value)}>
                            <option value="">Select months</option>
                            {monthOptions.map((m) => (
                              <option key={m} value={m}>{m} month{m > 1 ? 's' : ''} (+{(settings.bnpl_percent_per_month * m).toFixed(1)}%)</option>
                            ))}
                          </select>
                        </div>
                      )}

                      <h6 className="mb-2">Promo Code</h6>
                      {appliedCode ? (
                        <div className="alert alert-success d-flex justify-content-between align-items-center py-2">
                          <span>{promoMessage || `"${appliedCode}" applied`}</span>
                          <button type="button" className="btn btn-sm btn-link text-danger p-0" onClick={removePromo}>Remove</button>
                        </div>
                      ) : (
                        <div className="input-group mb-2">
                          <input
                            className="form-control"
                            placeholder="Enter promo code"
                            value={promoInput}
                            onChange={(e) => setPromoInput(e.target.value)}
                          />
                          <button className="btn btn-outline-primary" type="button" onClick={applyPromo}>Apply</button>
                        </div>
                      )}
                      {promoError && <div className="text-danger fs--1 mb-3">{promoError}</div>}

                      <hr />

                      <div className="d-flex justify-content-between mb-1">
                        <span>Subtotal</span>
                        <span>{quoting ? <Spinner animation="border" size="sm" /> : money(quote.subtotal)}</span>
                      </div>
                      {quote.discount > 0 && (
                        <div className="d-flex justify-content-between mb-1 text-success">
                          <span>Discount</span>
                          <span>-{money(quote.discount)}</span>
                        </div>
                      )}
                      {deliveryMethod === 'delivery' && (
                        <div className="d-flex justify-content-between mb-1">
                          <span>Delivery Fee</span>
                          <span>{money(quote.delivery_fee)}</span>
                        </div>
                      )}
                      {paymentMethod === 'bnpl' && bnplMonths && (
                        <div className="d-flex justify-content-between mb-1 text-warning">
                          <span>BNPL Markup ({bnplMarkupPercent().toFixed(1)}%)</span>
                          <span>{money(payable() - quote.total)}</span>
                        </div>
                      )}
                      <div className="d-flex justify-content-between fw-bold fs-0 mt-2 mb-4">
                        <span>Total</span>
                        <span>{money(payable())}</span>
                      </div>

                      <button
                        type="button"
                        className="btn btn-primary w-100"
                        disabled={placing || quoting}
                        onClick={placeOrder}
                      >
                        {placing ? (
                          <>
                            <Spinner animation="border" size="sm" /> Placing Order...
                          </>
                        ) : (
                          'Place Order'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <Footer />
          </div>

          <ToastContainer />
        </>
      )}
    </>
  );
};

export default Cart;
